import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../supabaseClient';
import GarageLogo from './GarageLogo';
import BugReport from './BugReport';
import './Header.css';

const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user?.id) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('is_admin')
            .eq('id', user.id)
            .single();
          
          if (!error && data) {
            setIsAdmin(data.is_admin || false);
          }
        } catch (error) {
          console.error('Error checking admin status:', error);
        }
      }
    };

    checkAdminStatus();
  }, [user]);

  // Load unread notifications count
  useEffect(() => {
    if (isAuthenticated && user) {
      loadUnreadCount();
      
      // Subscribe to real-time notification changes
      const channel = supabase
        .channel('notifications-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          () => {
            loadUnreadCount();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [isAuthenticated, user]);

  // Refresh count when dropdown opens
  useEffect(() => {
    if (showDropdown && isAuthenticated && user) {
      loadUnreadCount();
    }
  }, [showDropdown, isAuthenticated, user]);

  const loadUnreadCount = async () => {
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) throw error;
      setUnreadCount(count || 0);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
    navigate('/login');
  };

  return (
    <>
    <header className="app-header">
      <div className="header-content">
        <Link to="/" className="logo">
          <GarageLogo size={36} />
          <h2>GarageShare</h2>
        </Link>

        <nav className="nav-menu">
          {isAuthenticated ? (
            <>
              {user?.role === 'owner' && (
                <Link to="/my-garages" className="nav-link">
                  {t('header.myGarages')}
                </Link>
              )}
              
              {/* Notification Bell */}
              <div className="notification-bell">
                <button 
                  className="bell-button"
                  onClick={() => navigate('/notifications')}
                  title={t('header.notifications')}
                >
                  🔔
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                  )}
                </button>
              </div>
              
              <div className="profile-dropdown">
                <button 
                  className="profile-button"
                  onClick={() => setShowDropdown(!showDropdown)}
                >
                  {user?.profile_picture ? (
                    <img 
                      src={user.profile_picture} 
                      alt="Profile" 
                      className="profile-avatar profile-avatar-image"
                    />
                  ) : (
                    <div className="profile-avatar">
                      {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="profile-name">{user?.name || user?.email}</span>
                  <span className="dropdown-arrow">▼</span>
                </button>
                
                {showDropdown && (
                  <div className="dropdown-menu">
                    <div className="dropdown-header">
                      {user?.profile_picture ? (
                        <img 
                          src={user.profile_picture} 
                          alt="Profile" 
                          className="dropdown-avatar-image"
                        />
                      ) : (
                        <div className="dropdown-avatar">
                          {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="dropdown-user-info">
                        <strong>{user?.name || 'User'}</strong>
                        <p>{user?.email}</p>
                      </div>
                    </div>
                    <div className="dropdown-divider"></div>
                    {isAdmin && (
                      <Link 
                        to="/admin/dashboard" 
                        className="dropdown-item"
                        onClick={() => setShowDropdown(false)}
                      >
                        📊 {t('admin.title')}
                      </Link>
                    )}
                    <Link 
                      to="/profile" 
                      className="dropdown-item"
                      onClick={() => setShowDropdown(false)}
                    >
                      👤 {t('header.myProfile')}
                    </Link>
                    <Link 
                      to="/my-garages" 
                      className="dropdown-item"
                      onClick={() => setShowDropdown(false)}
                    >
                      🏠 {t('header.myGarages')}
                    </Link>
                    <Link 
                      to="/my-bookings" 
                      className="dropdown-item"
                      onClick={() => setShowDropdown(false)}
                    >
                      📅 {t('header.myBookings')}
                    </Link>
                    <Link 
                      to="/notifications" 
                      className="dropdown-item"
                      onClick={() => setShowDropdown(false)}
                    >
                      🔔 {t('header.notifications')}
                      {unreadCount > 0 && (
                        <span className="dropdown-badge">{unreadCount}</span>
                      )}
                    </Link>
                    <button 
                      onClick={() => {
                        setShowDropdown(false);
                        setShowBugReport(true);
                      }}
                      className="dropdown-item"
                    >
                      🐛 {t('header.reportBug')}
                    </button>
                    <div className="dropdown-divider"></div>
                    <button 
                      onClick={handleLogout} 
                      className="dropdown-item logout-item"
                    >
                      🚪 {t('header.logout')}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button onClick={() => navigate('/login')} className="nav-button-login">
                {t('header.login')}
              </button>
              <button onClick={() => navigate('/register')} className="nav-button">
                {t('header.signUp')}
              </button>
            </>
          )}
        </nav>
      </div>
    </header>

    {showBugReport && (
      <BugReport onClose={() => setShowBugReport(false)} />
    )}
    </>
  );
};

export default Header;